import {useEffect, useRef} from 'react';

const LAST_ACTIVITY_KEY = 'cm.lastActivity';
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;
const CHECK_INTERVAL_MS = 10_000;

/**
 * Automatically invokes {@code onTimeout} after the user has been inactive for
 * {@code timeoutMs} milliseconds, mirroring the backend's session inactivity timer.
 *
 * Activity is tracked by listening for user interaction events on {@code window}.
 * The timestamp of the last activity is stored in {@code localStorage} so that
 * activity in any open tab keeps the session alive across all tabs.
 *
 * The timeout is also checked immediately whenever the page becomes visible again
 * (e.g. after the browser was suspended or the user switched back to this tab),
 * ensuring a prompt logout even when the periodic interval was paused by the browser.
 *
 * ### Backend session synchronisation
 * The frontend timer is reset by user interaction events, but the backend session
 * timer is only reset by actual API calls. To keep both timers in sync,
 * {@code keepAlive} is called (throttled to at most once per {@code keepAliveIntervalMs})
 * whenever user activity is detected. This ensures the backend session stays alive as
 * long as the user is active, without polling when the user is idle.
 *
 * Note: the session timeout duration must match the backend's configured
 * inactivity timeout so the frontend and backend expire at the same time.
 *
 * @param timeoutMs           Inactivity timeout in milliseconds.
 * @param onTimeout           Callback invoked when the timeout is reached.
 * @param keepAlive           Optional callback to reset the backend session. Called
 *                            throttled on user activity. Should be a lightweight
 *                            authenticated request; errors should be swallowed by
 *                            the caller since session expiry is handled elsewhere.
 * @param keepAliveIntervalMs Minimum interval between {@code keepAlive} calls in
 *                            milliseconds. Defaults to 60 seconds.
 */
export function useSessionTimeout(
    timeoutMs: number,
    onTimeout: () => void,
    keepAlive?: () => void,
    keepAliveIntervalMs: number = 60_000,
): void
{
    // Store callbacks in refs to avoid re-running the effect when their identities change.
    const onTimeoutRef = useRef(onTimeout);
    onTimeoutRef.current = onTimeout;
    const keepAliveRef = useRef(keepAlive);
    keepAliveRef.current = keepAlive;

    const lastKeepAliveRef = useRef<number>(0);

    useEffect(() =>
    {
        function recordActivity(): void {
            localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());

            if (keepAliveRef.current && Date.now() - lastKeepAliveRef.current >= keepAliveIntervalMs) {
                lastKeepAliveRef.current = Date.now();
                keepAliveRef.current();
            }
        }

        function checkTimeout(): void {
            const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
            const lastActivity = raw !== null ? Number.parseInt(raw, 10) : Date.now();

            if (Date.now() - lastActivity >= timeoutMs) {
                onTimeoutRef.current();
            }
        }

        // Treat mounting as the first activity so the timer starts from now.
        recordActivity();

        ACTIVITY_EVENTS.forEach(event =>
            window.addEventListener(event, recordActivity, {passive: true})
        );

        // Check immediately when the tab becomes visible again (handles browser sleep /
        // tab switching where setInterval may have been throttled or paused).
        function onVisibilityChange(): void {
            if (document.visibilityState === 'visible') {
                checkTimeout();
            }
        }
        document.addEventListener('visibilitychange', onVisibilityChange);

        const intervalId = setInterval(checkTimeout, CHECK_INTERVAL_MS);

        return () => {
            ACTIVITY_EVENTS.forEach(event =>
                window.removeEventListener(event, recordActivity)
            );
            document.removeEventListener('visibilitychange', onVisibilityChange);
            clearInterval(intervalId);
        };
    }, [timeoutMs, keepAliveIntervalMs]);
}
